import React, { useState } from 'react'
import { FaEdit, FaEye, FaEyeSlash } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import { getCleanPdfName, handleDate } from '../handleDateAndPdf'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Swal from 'sweetalert2'
import toast from 'react-hot-toast'
import { deleteLatest, restoreReport, sendMail } from '../api/latestApi'

const DsjInsight = ({ data }) => {
  const queryClient = useQueryClient()
  const [toDelete, setToDelete] = useState(null)
  const [sendingMail, setSendingMail] = useState(false)

  const restoreMutation = useMutation({
    mutationFn: (id) => restoreReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['latestdealData'] })
      toast.success('Report restore sucessfully!')
    },
    onError: (err) => {
      console.error('restore report error:', err)
      toast.error(err.message || 'Failed to send verification email')
    },
  })

  // Send mail mutation with improved loading state
  const sendMailMutation = useMutation({
    mutationFn: (id) => sendMail(id),
    onMutate: () => {
      setSendingMail(true)
    },
    onSuccess: (response) => {
      setSendingMail(false)

      // Check if the response indicates success
      if (response?.status === 200 || response?.data) {
        toast.success(response?.data)
        // Show OTP modal after successful email send
        showOTPModal()
      } else {
        toast.error('Failed to send verification email')
        setToDelete(null)
      }
    },
    onError: (err) => {
      setSendingMail(false)
      console.error('Send mail error:', err)
      toast.error(err.message || 'Failed to send verification email')
      setToDelete(null)
    },
  })

  // Delete mutation with improved success handling
  const deleteMutation = useMutation({
    mutationFn: ({ id, otp }) => deleteLatest(id, otp),
    onSuccess: (response) => {
      // Invalidate and refetch latests
      queryClient.invalidateQueries({ queryKey: ['latestdealData'] })

      // Show success message
      toast.success('latest hide successfully!')

      // Additional success message with SweetAlert
      Swal.fire({
        title: 'Hide!',
        text: 'The latest has been hide successfully.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 3000,
        timerProgressBar: true,
      })

      // Reset state
      setToDelete(null)
    },
    onError: (err) => {
      console.error('Hide error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to hide latest. Please check your OTP.'
      toast.error(errorMessage)
    },
  })

  // Show OTP modal using SweetAlert with better UX
  const showOTPModal = () => {
    Swal.fire({
      title: 'Enter OTP',
      html: `
        <p>Please enter the OTP sent to your email</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
          <small style="color: #1e40af;">
            <i class="fas fa-info-circle"></i> Check your email inbox and spam folder
          </small>
        </div>
      `,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'Enter 6-digit OTP',
        maxlength: 6,
        style: 'text-align: center; font-size: 18px; letter-spacing: 2px;',
      },
      showCancelButton: true,
      confirmButtonText: 'Hide latest',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter OTP'
        }
        if (value.length !== 6) {
          return 'OTP must be 6 digits'
        }
        if (!/^\d+$/.test(value)) {
          return 'OTP must contain only numbers'
        }
      },
      preConfirm: (otp) => {
        return new Promise((resolve, reject) => {
          // Show loading state
          Swal.showLoading()

          // Update confirm button text while loading
          const confirmButton = Swal.getConfirmButton()
          const originalText = confirmButton.textContent
          confirmButton.textContent = 'Hiding...'

          // Call delete mutation with OTP
          deleteMutation.mutate(
            { id: toDelete, otp },
            {
              onSuccess: () => {
                resolve()
                Swal.close()
              },
              onError: (error) => {
                confirmButton.textContent = originalText
                Swal.hideLoading()
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Invalid OTP or deletion failed'
                Swal.showValidationMessage(`❌ ${errorMessage}`)
                reject(error)
              },
            }
          )
        })
      },
    })
      .then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          setToDelete(null)
          toast.info('Hide cancelled')
        }
      })
      .catch(() => {
        // Handle any unexpected errors
        setToDelete(null)
      })
  }

  // Handle delete click with improved UX
  const handleDeleteClick = (latestId, latestTitle) => {
    setToDelete(latestId)

    // Show confirmation dialog first
    Swal.fire({
      title: 'Hide latest?',
      html: `
          <p>Are you sure you want to Hide this latest?</p>
          <div style="margin: 10px 0; padding: 10px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #ef4444;">
            <strong style="color: #dc2626;">${latestTitle}</strong>
          </div>
          <p style="color: #6b7280; font-size: 14px;">
            This action will send an OTP to your email for verification.
          </p>
        `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send OTP',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Send email for OTP
        sendMailMutation.mutate(latestId)
      } else {
        setToDelete(null)
      }
    })
  }

  return (
    <>
      <div className="md:w-[90%] mx-auto my-6 overflow-x-auto whitespace-nowrap">
        <table className="min-w-full border border-gray-200 shadow-md rounded-lg">
          <thead className="bg-[#E9ECEF] text-gray-700">
            <tr>
              <th className="py-3 px-4 border-b">Icon</th>
              <th className="py-3 px-4 border-b">Brand Name</th>
              <th className="py-3 px-4 border-b">Company CIN</th>
              <th className="py-3 px-4 border-b">Funding Date</th>
              <th className="py-3 px-4 border-b">Price</th>
              <th className="py-3 px-4 border-b">Pdf</th>
              <th className="py-3 px-4 border-b">Description</th>
              <th className="py-3 px-4 border-b">Created At</th>
              <th className="py-3 px-4 border-b">Created By</th>
              <th className="py-3 px-4 border-b">Updated At</th>
              <th className="py-3 px-4 border-b">Updated By</th>
              <th className="py-3 px-4 border-b">Hide At</th>
              <th className="py-3 px-4 border-b">Hide By</th>
              <th className="py-3 px-4 border-b">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => {
              const date = new Date(item.fundingDate)
              const formattedDate = date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
              item.fundingDate = formattedDate
              return (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 text-gray-700 transition-colors duration-200"
                >
                  <td className="p-3">
                    <img
                      src={item.companyLogoUrl}
                      alt={item.companyName}
                      className="w-24 object-cover rounded"
                    />
                  </td>
                  <td className="py-3 px-4 border-b capitalize">
                    {item.companyName}
                  </td>
                  <td className="py-3 px-4 border-b">{item.cin}</td>

                  <td className="py-3 px-4 border-b">{item.fundingDate}</td>
                  <td className="py-3 px-4 border-b">&#8377;{item.pdfPrice}</td>
                  <td className="py-3 px-4 border-b">
                    {getCleanPdfName(item.pdfName)}
                  </td>
                  <td className="py-3 px-4 border-b max-w-lg">
                    {/* {item.fundingDetails} */}
                    <div className="truncate" title={item.fundingDetails}>
                      {item.fundingDetails}
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b">
                    {handleDate(item.createdAt)}
                  </td>
                  <td className="py-3 px-4 border-b">{item?.createAdmin}</td>
                  <td className="py-3 px-4 border-b">
                    {handleDate(item.updatedAt)}
                  </td>
                  <td className="py-3 px-4 border-b">{item?.updateAdmin}</td>

                  <td className="py-3 px-4 border-b">
                    {handleDate(item.deletedAt)}
                  </td>
                  <td className="py-3 px-4 border-b">{item?.deleteAdmin}</td>

                  <td className="py-3 px-4 border-b">
                    <div className="flex justify-center space-x-2">
                      {item?.deleted === false && (
                        <Link
                          to={
                            item.insights === 'funding365'
                              ? `/dealdashboard/add/funding/${item.id}`
                              : `/dealdashboard/add/latestdeal/${item.id}`
                          }
                          className="bg-orange-400 text-white p-2 rounded-md hover:bg-orange-500"
                          title="Edit"
                        >
                          <FaEdit />
                        </Link>
                      )}

                      {item?.deleted === true ? (
                        <button
                          title="Restore report"
                          onClick={() => restoreMutation.mutate(item.id)}
                          className="text-white bg-green-700 p-2 rounded hover:bg-green-900 transition-colors"
                        >
                          <FaEye />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteClick(item.id, item.companyName)
                          }
                          className={`text-white bg-red-500 p-2 rounded hover:bg-red-700 transition-colors ${
                            sendingMail || deleteMutation.isPending
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          title="Hide"
                          disabled={sendingMail || deleteMutation.isPending}
                        >
                          {sendingMail && toDelete === item.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                          ) : (
                            <FaEyeSlash />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {/* Loading overlay for sending email */}
        {sendingMail && (
          <div className="fixed inset-0 bg-black  font-aptos-regular bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-lg font-semibold">
                Sending verification email...
              </p>
              <p className="text-sm text-gray-500 mt-1">Please wait</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default DsjInsight
